/**
 * @module PagosOnlineContext
 * @description Contexto centralizado para el módulo de pagos en línea.
 * Provee todo el estado compartido, referencias DOM, datos maestros y funciones
 * de navegación del stepper a los sub-componentes del flujo de pago.
 *
 * Patrón utilizado: React Context + Provider para evitar prop-drilling
 * entre componentes profundamente anidados (StepperActions, DocumentoPago, etc.)
 */

import {
  createContext,
  useContext,
  FC,
  useState,
  useRef,
  useEffect,
  MutableRefObject,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import {
  valortotalDocumentosSeleccionados,
  baseUrl,
} from "../../helpers";

import {
  cargarDatosMaestros,
  realizarValidaciones,
  defaultState,
  consultarDocumentosPagoPersona,
} from "../../funcionesBase";

import {
  Idocumento,
  ServiciosVentas,
  TiposDocumentos,
  payLoadDatosMaestros,
  Iestado,
} from "../../helpers/dataExamples";

import { StepperComponent } from "../../components";
import * as Yup from "yup";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/** Interfaz vacía para los valores iniciales de Formik (sin campos requeridos) */
interface ICreateAccount {}

/** Valores iniciales del formulario Formik */
const inits: ICreateAccount = {};

/** Esquema de validación Yup por paso (actualmente sin reglas) */
const createAppSchema = [Yup.object({})];

/**
 * Interfaz que define la forma completa del contexto PagosOnline.
 * Centraliza estado, referencias, datos maestros y funciones de navegación.
 */
interface IPagosOnlineContext {
  /** Estado principal del flujo de pagos (datos del usuario, referencias, etc.) */
  estado: Iestado;
  setEstado: React.Dispatch<React.SetStateAction<Iestado>>;

  /** Índice del paso actual del stepper wizard (1, 2 o 3) */
  stepIndex: number;
  setStepIndex: React.Dispatch<React.SetStateAction<number>>;

  /** Indicador de carga del botón de envío (spinner) */
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;

  /** Bloqueo general de pantalla con overlay de carga (MyLoader) */
  pantallaBloqueada: boolean;
  setPantallaBloqueada: React.Dispatch<React.SetStateAction<boolean>>;

  /** Mensaje de error actual para el componente IAlerta */
  errorMensaje: string;
  setErrorMensaje: React.Dispatch<React.SetStateAction<string>>;

  /** Controla la visibilidad del componente IAlerta */
  showError: boolean;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;

  /** Lista de tipos de documento cargados desde el backend (C.C, T.I, NIT, etc.) */
  tiposDocumentosDisponibles: TiposDocumentos[];
  setTiposDocumentosDisponibles: React.Dispatch<
    React.SetStateAction<TiposDocumentos[]>
  >;

  /** Lista de servicios de venta disponibles (datos maestros del backend) */
  serviciosVentasDisponibles: ServiciosVentas[];
  setServiciosVentasDisponibles: React.Dispatch<
    React.SetStateAction<ServiciosVentas[]>
  >;

  /** Documentos de pago pendientes para la persona consultada */
  documentosPagoDisponibles: Idocumento[];
  setDocumentosPagoDisponibles: React.Dispatch<
    React.SetStateAction<Idocumento[]>
  >;

  /** Referencia a la instancia de StepperComponent para control de navegación */
  stepper: MutableRefObject<StepperComponent | null>;

  /** Referencia al contenedor div del stepper para inicialización */
  stepperRef: MutableRefObject<HTMLDivElement | null>;

  /** Referencia al select de tipo de documento */
  tipo_docu: MutableRefObject<HTMLSelectElement | null>;

  /** Referencia al input de número de documento */
  documento: MutableRefObject<HTMLInputElement | null>;

  /** Referencia al botón de continuar (usado para auto-click desde URL params) */
  continuar_btn: MutableRefObject<HTMLButtonElement | null>;

  /** Parámetros de ruta extraídos por react-router (ej: idpago para pago rápido) */
  parametrosURL: any;

  /** Query param: si es "true", oculta el logo de Comfenalco */
  ocultarLogo: string | null;

  /** Query param: si es "false", aplica clase CSS alternativa al card */
  showCard: string | null;

  /** Esquema de validación Yup activo según el paso actual */
  currentSchema: any;

  /** Valores iniciales del formulario Formik */
  initValues: ICreateAccount;

  /** Arreglo temporal (acumulador) usado por las funciones de consulta de documentos */
  documentosPago: Idocumento[];

  /** Navega al paso anterior del stepper */
  prevStep: () => void;

  /** Handler del submit de Formik (avanza paso o muestra confirmación) */
  submitStep: (values: ICreateAccount, actions: any) => void;

  /** Ejecuta las validaciones del paso actual y avanza si son exitosas */
  handleContinuar: () => void;
}

/**
 * Contexto React para el módulo PagosOnline.
 * Inicializado como undefined; el Provider lo llena con los valores reales.
 */
const PagosOnlineContext = createContext<IPagosOnlineContext | undefined>(
  undefined
);

/**
 * Hook personalizado para consumir el contexto de PagosOnline.
 * Incluye guarda de seguridad que lanza error si se usa fuera del Provider.
 * @returns El contexto completo de PagosOnline
 * @throws Error si se invoca fuera de PagosOnlineProvider
 */
const usePagosOnline = (): IPagosOnlineContext => {
  const context = useContext(PagosOnlineContext);
  if (!context) {
    throw new Error(
      "usePagosOnline debe usarse dentro de un PagosOnlineProvider"
    );
  }
  return context;
};

/**
 * Provider del contexto PagosOnline.
 * Inicializa todo el estado, carga los datos maestros al montar,
 * gestiona los efectos de auto-relleno por query params y expone
 * funciones de navegación del stepper.
 *
 * @param children - Componentes hijos que tendrán acceso al contexto
 */
const PagosOnlineProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const parametrosURL = useParams();
  const MySwal = withReactContent(Swal);

  // ─── Estados del flujo ─────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [currentSchema, setCurrentSchema] = useState(createAppSchema[0]);
  const [initValues] = useState<ICreateAccount>(inits);
  const [pantallaBloqueada, setPantallaBloqueada] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [stepIndex, setStepIndex] = useState(1);
  const [showError, setShowError] = useState(false);
  const [estado, setEstado] = useState(defaultState);

  // ─── Datos maestros (arrays temporales + estado) ───────────────────
  /** Array temporal de servicios de venta, usado como acumulador en cargarDatosMaestros */
  let serviciosVentas: Array<ServiciosVentas> = [];
  const [serviciosVentasDisponibles, setServiciosVentasDisponibles] =
    useState(serviciosVentas);

  /** Array temporal de tipos de documento, usado como acumulador en cargarDatosMaestros */
  let tiposDocumentos: Array<TiposDocumentos> = [];
  const [tiposDocumentosDisponibles, setTiposDocumentosDisponibles] =
    useState(tiposDocumentos);

  /** Array temporal de documentos de pago, usado como acumulador en consultarDocumentosPago */
  let documentosPago: Array<Idocumento> = [];
  const [documentosPagoDisponibles, setDocumentosPagoDisponibles] =
    useState(documentosPago);

  // ─── Referencias DOM ───────────────────────────────────────────────
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);
  const tipo_docu = useRef<HTMLSelectElement>(null);
  const documento = useRef<HTMLInputElement>(null);
  const continuar_btn = useRef<HTMLButtonElement>(null);

  // ─── Query params de la URL ────────────────────────────────────────
  const queryParams = new URLSearchParams(window.location.search);
  const ocultarLogo = queryParams.get("ocultarLogo");
  const cedula = queryParams.get("cedula");
  const tdocumento = queryParams.get("tdocumento");
  const showCard = queryParams.get("showCard");

  /**
   * Inicializa la instancia del componente Stepper a partir del contenedor DOM.
   * Se ejecuta cuando stepperRef está disponible.
   */
  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(
      stepperRef.current as HTMLDivElement
    );
  };

  /**
   * Navega al paso anterior del stepper.
   * Limpia errores previos y reconsulta documentos si regresa al paso 2.
   */
  const prevStep = () => {
    setErrorMensaje("");
    setShowError(false);
    if (!stepper.current) return;

    stepper.current.goPrev();
    setStepIndex(stepper.current.currentStepIndex);
    setCurrentSchema(createAppSchema[stepper.current.currentStepIndex - 1]);

    // Re-consulta documentos de pago al volver al paso de selección
    if (stepper.current.currentStepIndex === 2) {
      consultarDocumentosPagoPersona(
        estado,
        setPantallaBloqueada,
        documentosPago,
        setDocumentosPagoDisponibles,
        setEstado
      );
    }
  };

  /**
   * Handler del submit de Formik.
   * Avanza al siguiente paso si no es el último, o muestra confirmación de éxito.
   * Se bloquea si hay errores visibles (showError === true).
   */
  const submitStep = (values: ICreateAccount, actions: any) => {
    if (!stepper.current || showError) return;

    setCurrentSchema(createAppSchema[stepper.current.currentStepIndex]);

    if (stepper.current.currentStepIndex !== stepper.current.totatStepsNumber) {
      stepper.current.goNext();
      setStepIndex(stepper.current.currentStepIndex);
    } else {
      setLoading(true);
      setTimeout(() => {
        MySwal.fire({
          icon: "success",
          title: <p>Pago realizado con exito</p>,
          footer: "Comfenalco 2021",
        }).then(() => {
          setLoading(false);
          stepper.current?.goto(1);
          actions.resetForm();
        });
      }, 2000);
    }
  };

  /**
   * Ejecuta las validaciones correspondientes al paso actual del stepper.
   * Delega a realizarValidaciones (funcionesBase) que contiene la lógica de:
   * - Paso 1: Validar documento e identidad del usuario
   * - Paso 2: Validar montos de abono y referencias seleccionadas
   */
  const handleContinuar = () => {
    realizarValidaciones(
      estado,
      showError,
      stepper,
      stepIndex,
      documentosPago,
      setErrorMensaje,
      setShowError,
      setPantallaBloqueada,
      setEstado,
      setStepIndex,
      setDocumentosPagoDisponibles
    );
  };

  // ─── Efectos ───────────────────────────────────────────────────────

  /** Inicializa el stepper cuando el contenedor DOM está disponible */
  useEffect(() => {
    if (!stepperRef.current) return;
    loadStepper();
  }, [stepperRef]);

  /**
   * Carga datos maestros al montar el componente:
   * - Tipos de documento (desde API)
   * - Servicios de venta (desde API)
   * - Si hay idpago en URL, ejecuta flujo de pago rápido
   */
  useEffect(() => {
    const payload: payLoadDatosMaestros = {
      tiposDocumentos,
      serviciosVentas,
      parametrosURL,
      estado,
      stepper,
      setEstado,
      setStepIndex,
      setPantallaBloqueada,
      setServiciosVentasDisponibles,
      setTiposDocumentosDisponibles,
      setErrorMensaje,
      setShowError,
    };

    cargarDatosMaestros(payload);
  }, []);

  /**
   * Auto-relleno: si se reciben cédula y tipo de documento por query params,
   * pre-completa los campos y simula click en "Continuar" para avanzar automáticamente.
   * Útil para integración con sistemas externos que redirigen con datos pre-cargados.
   */
  useEffect(() => {
    if (cedula !== null && tdocumento !== null) {
      const newState = { ...estado };
      newState.documento = cedula;
      newState.tipoDocumento = tdocumento;
      setEstado(newState);

      // Timeout de 1ms para permitir que el botón se renderice antes del click
      setTimeout(() => {
        continuar_btn.current?.click();
      }, 1);
    }
  }, []);

  // ─── Valor del contexto ────────────────────────────────────────────
  const value: IPagosOnlineContext = {
    estado,
    setEstado,
    stepIndex,
    setStepIndex,
    loading,
    setLoading,
    pantallaBloqueada,
    setPantallaBloqueada,
    errorMensaje,
    setErrorMensaje,
    showError,
    setShowError,
    tiposDocumentosDisponibles,
    setTiposDocumentosDisponibles,
    serviciosVentasDisponibles,
    setServiciosVentasDisponibles,
    documentosPagoDisponibles,
    setDocumentosPagoDisponibles,
    stepper,
    stepperRef,
    tipo_docu,
    documento,
    continuar_btn,
    parametrosURL,
    ocultarLogo,
    showCard,
    currentSchema,
    initValues,
    documentosPago,
    prevStep,
    submitStep,
    handleContinuar,
  };

  return (
    <PagosOnlineContext.Provider value={value}>
      {children}
    </PagosOnlineContext.Provider>
  );
};

export { PagosOnlineContext, PagosOnlineProvider, usePagosOnline };
